import React from 'react';
import { ArrowRight, Mic, MessageSquareQuote, ListChecks, Radio } from 'lucide-react';
import useReveal from '../../hooks/useReveal';

/**
 * AI Interview simulator.
 *
 * Inverted (dark) section - a deliberate pause in the white rhythm of the page
 * that makes this feature feel like a distinct product rather than another
 * bullet, and matches the focused, slightly serious mood of an interview.
 */

const STEPS = [
  { icon: Radio, title: 'Pick a role', copy: 'Full-stack, backend, data, frontend and more.' },
  { icon: MessageSquareQuote, title: 'Answer out loud', copy: 'Seven questions, generated fresh for every attempt.' },
  { icon: ListChecks, title: 'Get a scored report', copy: 'Per-question feedback and an overall score.' }
];

const InterviewSection = () => {
  const ref = useReveal({ stagger: 100 });

  return (
    <section style={{ background: 'var(--bg-ink)' }}>
      <div ref={ref} className="section">
        <div className="grid items-center gap-14 lg:grid-cols-2 lg:gap-20">
          {/* ------------------------------ Copy ------------------------------ */}
          <div>
            <p
              className="eyebrow reveal"
              style={{ color: '#8ab4e8' }}
            >
              AI Interview
            </p>
            <h2
              className="heading-2 reveal mt-3"
              style={{ color: '#fff' }}
            >
              Practise the interview
              <br />
              before it counts
            </h2>
            <p
              className="body-large reveal mt-5 max-w-[520px]"
              style={{ color: 'rgba(255,255,255,0.66)' }}
            >
              Speak your answers to role-specific questions and get an honest, scored
              breakdown of how you came across - so the real interview is not the first
              time you have said any of it out loud.
            </p>

            <ul className="mt-9 space-y-5">
              {STEPS.map(({ icon: Icon, title, copy }) => (
                <li key={title} className="reveal flex gap-4">
                  <span
                    className="grid h-10 w-10 shrink-0 place-items-center rounded-xl"
                    style={{ background: 'rgba(255,255,255,0.07)', color: '#8ab4e8' }}
                  >
                    <Icon className="h-4.5 w-4.5" style={{ width: 18, height: 18 }} />
                  </span>
                  <div>
                    <p className="font-semibold" style={{ color: '#fff' }}>{title}</p>
                    <p className="mt-0.5 text-[0.92rem]" style={{ color: 'rgba(255,255,255,0.58)' }}>
                      {copy}
                    </p>
                  </div>
                </li>
              ))}
            </ul>

            <div className="reveal mt-9">
              <a href="https://app.tenses.ai" className="btn-primary">
                Try an interview
                <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </div>

          {/* ----------------------------- Visual ----------------------------- */}
          <div className="reveal">
            <InterviewMock />
          </div>
        </div>
      </div>
    </section>
  );
};

/** A stylised interview screen: question, live waveform, and a scored answer. */
const InterviewMock = () => (
  <div
    className="relative mx-auto w-full max-w-[460px] rounded-[28px] p-6"
    style={{
      background: 'rgba(255,255,255,0.05)',
      border: '1px solid rgba(255,255,255,0.1)',
      boxShadow: '0 40px 80px rgba(0,0,0,0.45)'
    }}
  >
    {/* Header */}
    <div className="mb-5 flex items-center justify-between">
      <span
        className="rounded-full px-2.5 py-1 text-[0.7rem] font-semibold"
        style={{ background: 'rgba(138,180,232,0.16)', color: '#8ab4e8' }}
      >
        Question 3 of 7
      </span>
      <span className="text-[0.72rem] tabular-nums" style={{ color: 'rgba(255,255,255,0.45)' }}>
        01:12
      </span>
    </div>

    {/* Question */}
    <p
      className="text-[1.05rem] leading-relaxed"
      style={{ color: 'rgba(255,255,255,0.92)' }}
    >
      “Tell me about a time you had to explain a technical decision to someone
      non-technical.”
    </p>

    {/* Recording state */}
    <div
      className="mt-6 flex items-center gap-3 rounded-2xl px-4 py-3.5"
      style={{ background: 'rgba(255,255,255,0.05)' }}
    >
      <span
        className="grid h-10 w-10 shrink-0 place-items-center rounded-full"
        style={{ background: '#dc2626' }}
      >
        <Mic className="h-4 w-4" style={{ color: '#fff' }} />
      </span>

      {/* Waveform - pure CSS bars, each with its own rhythm. */}
      <div className="flex h-9 flex-1 items-center gap-[3px]" aria-hidden="true">
        {[9, 17, 28, 14, 33, 22, 36, 19, 26, 12, 30, 20, 34, 15, 24, 10, 29, 18, 32, 13].map(
          (h, i) => (
            <span
              key={i}
              className="w-[3px] rounded-full"
              style={{
                height: `${h}px`,
                background: i % 3 === 0 ? '#8ab4e8' : 'rgba(255,255,255,0.32)',
                animation: `float-soft ${1.1 + (i % 5) * 0.22}s ease-in-out ${i * 0.05}s infinite`
              }}
            />
          )
        )}
      </div>

      <span className="text-[0.72rem] font-medium" style={{ color: 'rgba(255,255,255,0.5)' }}>
        Recording
      </span>
    </div>

    {/* Previous answer, already scored */}
    <div
      className="mt-4 rounded-2xl p-4"
      style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}
    >
      <div className="flex items-center justify-between">
        <span className="text-[0.75rem] font-medium" style={{ color: 'rgba(255,255,255,0.55)' }}>
          Question 2 · scored
        </span>
        <span className="text-[0.8rem] font-bold tabular-nums" style={{ color: '#8FEC78' }}>
          84/100
        </span>
      </div>
      <p className="mt-2 text-[0.82rem] leading-relaxed" style={{ color: 'rgba(255,255,255,0.6)' }}>
        Clear structure and a concrete example. Could quantify the impact and tighten the
        opening.
      </p>
    </div>
  </div>
);

export default InterviewSection;
